import type { IconName } from "@introist/react-foundation/v2";
import type { Scope } from "@introist/introist-api-types/dist/auth";
import { isAuthorizedAs, IntroistUser, UserRole } from "IdentityProvider";
import { RouterInput } from "services/rpc/RpcProvider";

export type SupportResource = NonNullable<RouterInput["support"]["send"]["resources"]>[number];
export type SupportResourceType = SupportResource["type"];

// Only the tag-relevant fields go to the API; the label lives in the drawer
export type AttachedResource = { resource: SupportResource; label?: string };

export const resourceKey = (resource: SupportResource) => {
  switch (resource.type) {
    case "workflowStep":
      return `workflow/${resource.workflowId}/step/${resource.id}`;
    case "automationStep":
      return `automation/${resource.automationId}/step/${resource.id}`;
    default:
      return `${resource.type}/${resource.id}`;
  }
};

type ResourceTypeConfig = {
  title: string;
  icon: IconName;
  // Any one scope from each group is needed; the list endpoints throw FORBIDDEN otherwise
  scopes: Scope[][];
};

const workflowScopes: Scope[] = ["workflows:read", "workflows:read_all"];

export const RESOURCE_TYPES: Record<SupportResourceType, ResourceTypeConfig> = {
  workflow: { title: "Workflow", icon: "workflows", scopes: [workflowScopes] },
  workflowStep: { title: "Workflow step", icon: "stepType", scopes: [workflowScopes] },
  trigger: { title: "Trigger", icon: "trigger", scopes: [["triggers:read_all"]] },
  employee: { title: "Employee", icon: "person", scopes: [["employees:read"]] },
  form: {
    title: "Form",
    icon: "form",
    scopes: [["forms:read_all", "workflows:write", "workflows:write_all"]]
  },
  automation: {
    title: "Automation",
    icon: "condition",
    scopes: [workflowScopes, ["employees:read"]]
  },
  automationStep: {
    title: "Automation step",
    icon: "stepType",
    scopes: [workflowScopes, ["employees:read"]]
  }
};

export const canPick = (user: IntroistUser, type: SupportResourceType) =>
  isAuthorizedAs(user, UserRole.Admin) ||
  RESOURCE_TYPES[type].scopes.every(group => group.some(s => user.scopes?.includes(s)));
